import { useEffect, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import store from './store';
import { Provider } from 'react-redux';
import { Router, Switch, Route } from 'react-router-dom';
// eslint-disable-next-line
import './App.css';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import 'lazysizes';
import PrivateRoute from './services/PrivateRoute';
import ErrorBoundary from './services/ErrorBoundary';
import { updateLocalAuthState } from './actions/auth';
import { NotFound, history } from 'cademy-shared-web';
import Home from './routes/Home';
import Order from './routes/Order';
import Booking from './routes/Booking';
import CompleteBooking from './routes/CompleteBooking';
import CompleteOrder from './routes/CompleteOrder';
import { ThemeProvider } from 'evergreen-ui';
import { theme } from './theme.js';
import { setUtms } from './actions/profile';
import SocketContext from './services/SocketContext';
import Logout from 'routes/Logout';
import getSubdomain from 'services/getSubdomain';
import { filterApprovedUtms } from 'services/utmHelpers';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import BugsnagPerformance from '@bugsnag/browser-performance';

if (
    process.env.REACT_APP_ENVIRONMENT === 'Production' ||
    process.env.REACT_APP_ENVIRONMENT === 'Stage'
) {
    Bugsnag.start({
        apiKey: process.env.REACT_APP_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginReact()],
        releaseStage: process.env.REACT_APP_ENVIRONMENT,
    });
    BugsnagPerformance.start({ apiKey: process.env.REACT_APP_BUGSNAG_API_KEY });
}

const RenderRoutes = () => {
    const subdomain = getSubdomain();
    if (subdomain) {
        return (
            <Switch>
                <PrivateRoute
                    exact
                    path="/bookings/:booking_param"
                    component={Booking}
                    subdomain={subdomain}
                />
                <PrivateRoute
                    exact
                    path="/orders/:order_param"
                    component={Order}
                    subdomain={subdomain}
                />
                <Route exact path="/logout" children={<Logout />} />

                <Route component={NotFound} />
            </Switch>
        );
    }

    return (
        <Switch>
            <PrivateRoute exact path="/bookings/:booking_param" component={Booking} />
            <PrivateRoute exact path="/orders/:order_param" component={Order} />
            <Route exact path="/" component={Home} />
            <Route exact path="/logout" children={<Logout />} />

            <Route exact path="/complete-booking/:booking_id" component={CompleteBooking} />
            <Route exact path="/complete-order/:order_id" component={CompleteOrder} />

            <Route component={NotFound} />
        </Switch>
    );
};

function App() {
    const [queryClient] = useState(() => new QueryClient());

    useEffect(() => {
        store.dispatch(updateLocalAuthState());

        try {
            const urlSearchParams = new URLSearchParams(window.location.search);
            const urlParams = Object.fromEntries(urlSearchParams.entries());
            delete urlParams.token;
            delete urlParams.fallback_email;
            const localItem = localStorage.getItem('cademyUtms');
            const localParams = localItem ? JSON.parse(localItem) : {};
            const referrer = localParams.referrer || urlParams?.referrer || document.referrer;
            const combined = {
                ...localParams,
                ...urlParams,
                referrer,
            };
            const allowedUtmsOnly = filterApprovedUtms(combined);

            localStorage.setItem('cademyUtms', JSON.stringify(combined));
            store.dispatch(
                setUtms({
                    ...combined,
                    utmsQueryString: new URLSearchParams(allowedUtmsOnly).toString(),
                    redirectRef: new URLSearchParams(combined).toString(),
                })
            );
        } catch (e) {}

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Provider store={store}>
            <Router history={history}>
                <ErrorBoundary>
                    <QueryClientProvider client={queryClient}>
                        <ThemeProvider value={theme}>
                            <SocketContext>
                                <RenderRoutes />
                            </SocketContext>
                        </ThemeProvider>
                    </QueryClientProvider>
                </ErrorBoundary>
            </Router>
        </Provider>
    );
}

export default App;
